import React, { useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Loader2, UserCheck, Calendar, MapPin, AlertTriangle, Search } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatTimeRange } from '../../utils/time';
import { toast } from 'sonner';

const SERVICE_LABEL = {
  day_program: 'Day Program',
  employment: 'Employment',
  in_home: 'IHCS',
  private_pay: 'Private Pay',
};
const SERVICE_TONE = {
  day_program: { bg: '#EDE7F6', fg: '#5A2F8E', border: '#C8B6E8' },
  employment: { bg: '#E3F2FD', fg: '#0D47A1', border: '#90CAF9' },
  in_home: { bg: '#FFF3E0', fg: '#E65100', border: '#FFB74D' },
  private_pay: { bg: '#E8F5E9', fg: '#1B5E20', border: '#A5D6A7' },
};

// ============================================================
// "Open Shifts" — shows every unassigned shift in the current
// view (week or month) with inline assign-to-staff capability.
// ============================================================
export default function UnassignedShiftsDialog({
  open, onClose, shifts, employees, participants, api, onAssigned, viewLabel,
}) {
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [assigning, setAssigning] = useState({}); // { shiftId: employeeId }
  const [saving, setSaving] = useState({});       // { shiftId: bool }

  const participantById = useMemo(() => {
    const m = {};
    (participants || []).forEach(p => {
      if (p.id) m[p.id] = p;
      if (p.name) m[p.name] = p;
    });
    return m;
  }, [participants]);

  // Participants can be stored as IDs *or* as plain name strings — resolve either.
  const resolveParticipantName = (p) => {
    if (!p) return '';
    if (typeof p === 'string') return participantById[p]?.name || p;
    return p.name || participantById[p.id]?.name || '';
  };

  const unassigned = useMemo(() => {
    return (shifts || [])
      .filter(s => !s.employee_id || s.employee_id === '')
      .filter(s => serviceFilter === 'all' || s.service_line === serviceFilter)
      .filter(s => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const partNames = (s.participants || [])
          .map(resolveParticipantName)
          .join(' ')
          .toLowerCase();
        return (
          (s.location || '').toLowerCase().includes(q)
          || (s.notes || '').toLowerCase().includes(q)
          || (s.service_line || '').toLowerCase().includes(q)
          || partNames.includes(q)
          || (s.date || '').includes(q)
        );
      })
      .sort((a, b) => {
        // Sort by date ascending, then time
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.time_in || '').localeCompare(b.time_in || '');
      });
  }, [shifts, search, serviceFilter, participantById]);

  // Group by date
  const grouped = useMemo(() => {
    const out = {};
    unassigned.forEach(s => {
      if (!out[s.date]) out[s.date] = [];
      out[s.date].push(s);
    });
    return out;
  }, [unassigned]);

  // Eligible employees per service line
  const eligibleEmployees = useCallback((service) => {
    return (employees || [])
      .filter(e => !e.archived && !e.hide_from_scheduling)
      .filter(e => !service || !e.service_lines?.length || e.service_lines.includes(service))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [employees]);

  const handleAssign = async (shift) => {
    const employeeId = assigning[shift.id];
    if (!employeeId) {
      toast.error('Pick a staff member first');
      return;
    }
    setSaving(prev => ({ ...prev, [shift.id]: true }));
    try {
      const payload = {
        employee_id: employeeId,
        service_line: shift.service_line,
        date: shift.date,
        time_in: shift.time_in,
        time_out: shift.time_out,
        location: shift.location || '',
        notes: shift.notes || '',
        participants: shift.participants || [],
        group_number: shift.group_number ?? null,
        is_draft: !!shift.is_draft,
        program_id: shift.program_id || '',
      };
      await api.put(`/shifts/${shift.id}`, payload);
      const emp = employees.find(e => e.id === employeeId);
      toast.success(`Assigned to ${emp?.name || 'staff'}`);
      onAssigned?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || detail?.code || 'Assignment failed';
      toast.error(msg);
    }
    setSaving(prev => ({ ...prev, [shift.id]: false }));
  };

  const total = unassigned.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl bg-white max-h-[85vh] flex flex-col" data-testid="open-shifts-dialog">
        <DialogHeader>
          <DialogTitle
            className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="h-5 w-5 text-[#E65100]" />
            Open Shifts
            <span
              className="ml-1 text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ background: total > 0 ? '#FFE5E5' : '#E8F5E9', color: total > 0 ? '#D9534F' : '#1B5E20' }}>
              {total} unassigned
            </span>
            {viewLabel && <span className="text-xs font-normal text-[#8E85A0] ml-1">· {viewLabel}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E85A0]" />
            <Input
              placeholder="Search participant, location, date…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border-[#DED8EB] pl-8 h-9 text-sm"
              data-testid="open-shifts-search"
            />
          </div>
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger className="border-[#DED8EB] w-44 h-9 text-sm" data-testid="open-shifts-service-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All services</SelectItem>
              <SelectItem value="day_program">Day Program</SelectItem>
              <SelectItem value="employment">Employment</SelectItem>
              <SelectItem value="in_home">IHCS</SelectItem>
              <SelectItem value="private_pay">Private Pay</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="overflow-y-auto -mx-6 px-6 flex-1 mt-2" data-testid="open-shifts-list">
          {total === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="h-10 w-10 mx-auto text-[#1B5E20] mb-3" />
              <p className="text-base font-semibold text-[#2D1B4E]">All shifts are assigned.</p>
              <p className="text-sm text-[#8E85A0] mt-1">Nothing open in this view.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([date, dateShifts]) => (
              <div key={date} className="mb-4" data-testid={`open-shifts-day-${date}`}>
                <div className="sticky top-0 bg-white pt-1 pb-1.5 z-10 border-b border-[#F0EDF6]">
                  <h4 className="text-xs uppercase tracking-widest font-bold text-[#6B3FA0]">
                    <Calendar className="h-3 w-3 inline mr-1.5 -mt-0.5" />
                    {format(parseISO(date), 'EEEE · MMM d')}
                    <span className="ml-2 text-[#8E85A0] font-normal normal-case tracking-normal">
                      {dateShifts.length} open
                    </span>
                  </h4>
                </div>

                <div className="space-y-1.5 mt-2">
                  {dateShifts.map(shift => {
                    const tone = SERVICE_TONE[shift.service_line] || SERVICE_TONE.day_program;
                    const partNames = (shift.participants || [])
                      .map(resolveParticipantName)
                      .filter(Boolean);
                    return (
                      <div
                        key={shift.id}
                        className="border border-[#DED8EB] rounded-lg p-3 flex items-center gap-3 flex-wrap hover:border-[#6B3FA0] transition"
                        data-testid={`open-shift-${shift.id}`}>
                        <span
                          className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded shrink-0"
                          style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}>
                          {SERVICE_LABEL[shift.service_line] || shift.service_line}
                        </span>
                        <div className="text-sm font-semibold text-[#2D1B4E] tabular-nums shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {formatTimeRange(shift.time_in, shift.time_out)}
                        </div>
                        <div className="flex-1 min-w-[160px] text-xs text-[#5A4D6E]">
                          {partNames.length > 0 && (
                            <div className="font-medium text-[#2D1B4E] truncate">
                              {partNames.join(', ')}
                              {shift.group_number != null && (
                                <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 bg-[#EDE7F6] text-[#5A2F8E] rounded">G{shift.group_number}</span>
                              )}
                            </div>
                          )}
                          {shift.location && (
                            <div className="flex items-center gap-1 text-[#8E85A0] mt-0.5">
                              <MapPin className="h-3 w-3" /> {shift.location}
                            </div>
                          )}
                        </div>

                        {/* Inline assign */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Select
                            value={assigning[shift.id] || ''}
                            onValueChange={(v) => setAssigning(prev => ({ ...prev, [shift.id]: v }))}>
                            <SelectTrigger
                              className="border-[#DED8EB] w-44 h-8 text-xs"
                              data-testid={`assign-select-${shift.id}`}>
                              <SelectValue placeholder="Pick staff…" />
                            </SelectTrigger>
                            <SelectContent>
                              {eligibleEmployees(shift.service_line).map(e => (
                                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            disabled={!assigning[shift.id] || saving[shift.id]}
                            onClick={() => handleAssign(shift)}
                            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8 gap-1"
                            data-testid={`assign-btn-${shift.id}`}>
                            {saving[shift.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <UserCheck className="h-3 w-3" />
                            )}
                            {saving[shift.id] ? '' : 'Assign'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {total > 0 && (
          <div className="border-t border-[#F0EDF6] pt-3 text-xs text-[#8E85A0] italic">
            Tip: choose a staff member from the dropdown and click Assign. Conflicts will be caught automatically — you'll get a heads-up if anyone is double-booked.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
